<template>
  <div class="project-worklogs">
    <section class="section">
      <div class="container">
        <div class="columns">
          <div class="column is-full">
            <div class="box">
              <div class="columns" style="margin-top: 16px">
                <div class="column">
                  <header class="heading">
                    <p class="modal-card-title">{{ pageTitle }}
                      <router-link class="has-text-weight-bold"
                                   :to="{ name: 'ProjectDetails', params: { id:project_id }}">
                        {{ projectCode }}
                      </router-link>
                    </p>
                  </header>
                </div>
                <div class="column is-narrow">
                  <b-button class="button" @click="backClicked" tabindex="1" icon-left="arrow-left-bold">
                    {{ $t('common.back') }}
                  </b-button>
                </div>
                <div v-if="this.currentUserAccess('ProjectSummary', UserActions.VIEW, this.project_id)"
                     class="column is-narrow">
                  <b-button class="button" @click="summaryClicked" icon-left="chart-gantt">
                    <router-link :to="{ name: 'ProjectSummary', params: { project_id:this.project_id }}">
                      {{ $t('projects-view.show-summary') }}
                    </router-link>
                  </b-button>
                </div>
              </div>
              <div class="columns">
                <div class="column is-narrow is-mobile">
                  <b-datepicker
                      :mobile-native="false"
                      v-model="modelDate"
                      :placeholder="$t('project-logs.select-date')"
                      icon="calendar-today"
                      locale="fr-FR"
                      show-week-number
                      @input="dateSelectClicked">
                  </b-datepicker>
                </div>
                <div class="column is-narrow is-mobile">
                  <b-button class="button is-primary is-mobile" icon-left="content-duplicate"
                            @click="duplicateLogs">
                    {{ $t('project-logs.duplicate-logs') }}
                  </b-button>
                </div>
              </div>
              <FastProjectTaskLog v-bind:project_id="this.project_id" v-bind:selected_date="this.modelDate"
                                  @allLogsCreated="onAllLogsCreated"/>
              <div class="columns" style="margin-top: 16px">
                <div class="column is-full">
                  <b-tabs type="is-boxed">
                    <b-tab-item :label="$t('tasks-logs.component-title')" icon="format-list-checks">
                      <TasksLogs v-bind:project_id="this.project_id" v-bind:selected_date="selectedDate"/>
                    </b-tab-item>
                    <b-tab-item :label="$t('personnel-worklogs.component-title')" icon="account-hard-hat">
                      <PersonnelWorklogs v-bind:project_id="this.project_id" v-bind:selected_date="selectedDate"/>
                    </b-tab-item>
                    <b-tab-item :label="$t('equipments-logs.component-title')" icon="robot-industrial">
                      <EquipmentsLogs v-bind:project_id="this.project_id" v-bind:selected_date="selectedDate"/>
                    </b-tab-item>
                    <b-tab-item :label="$t('consumables-logs.component-title')" icon="cart-variant">
                      <ConsumablesLogs v-bind:project_id="this.project_id" v-bind:selected_date="selectedDate"/>
                    </b-tab-item>
                  </b-tabs>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <b-modal
          :width="640"
          v-model="isDuplicateLogsModalActive"
          trap-focus
          :destroy-on-hide="true"
          aria-role="dialog"
          aria-modal>
        <template #default="props">
          <DuplicateLogsDetails v-bind:project_id="project_id" v-bind:passed_source_date="selectedDate"
                                @close="props.close"
                                @logsDuplicated="onDuplicateSuccess"></DuplicateLogsDetails>
        </template>
      </b-modal>
    </section>
  </div>
</template>

<script>

import TasksLogs from "@/components/TasksLogs";
import PersonnelWorklogs from "@/components/PersonnelWorklogs";
import EquipmentsLogs from "@/components/EquipmentsLogs";
import ConsumablesLogs from "@/components/ConsumablesLogs";
import firebase from "firebase/app";
import FastProjectTaskLog from "@/components/FastProjectTaskLog";
import DuplicateLogsDetails from "@/components/DuplicateLogsDetails";

export default {
  name: "ProjectLogs",
  components: {
    DuplicateLogsDetails,
    FastProjectTaskLog,
    TasksLogs,
    PersonnelWorklogs,
    EquipmentsLogs,
    ConsumablesLogs
  },
  props: {
    'project_id': {
      type: String,
    }
  },
  data() {
    return {
      modelDate: new Date(),
      projectCode: "",
      isDuplicateLogsModalActive: false,
    }
  },
  methods: {
    duplicateLogs: function () {
      this.isDuplicateLogsModalActive = true;
    },
    readProjectData: function () {
      const functions = firebase.app().functions("europe-west3");
      const getProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getProjectFunction({action: "get_project_by_id", document_id: this.project_id, fill_meta: true})
          .then((result) => {
            console.log("result get project by id: ", result.data);
            this.projectCode = result.data.projects.code;
          })
          .catch((error) => {
            this.showError(error.message)
            if (error.message === "InvalidPath") {
              this.$router.replace("../projects");
            }
            console.log("Error getting project: ", error);
          });
    },
    onDuplicateSuccess: function () {
      this.isDuplicateLogsModalActive = false;
    },
    onAllLogsCreated: function () {
      // Update `modelDate` to ensure the components use the latest date and gets refreshed!
      const currentSelectedDate = new Date(this.modelDate);
      this.modelDate = new Date()
      this.modelDate = new Date(currentSelectedDate.getTime() + 1)
    },
    /*
     UI Events
     */
    dateSelectClicked: function (date) {
      this.modelDate = date
    },
    backClicked: function () {
      this.$router.back()
    },
    summaryClicked: function () {
      this.$router.push({
        name: 'ProjectSummary',
        params: {
          project_id: this.project_id
        }
      })
    }
  },
  mounted() {
    if (!this.currentUserAccess("ProjectLogs", this.UserActions.VIEW, this.project_id)) {
      console.log("Page access denied.");
      this.$router.replace("/projects/").catch(() => {
      });
    }
    this.readProjectData();
  },
  computed: {
    pageTitle() {
      return this.$t('project-logs.page-title')
    },
    selectedDate() {
      return this.modelDate;
    },
  }
};
</script>
